'use client';

import { memo, useEffect, useState, type FC, type ReactNode } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronRight, Gauge, Gavel, MapPin, Tag, Wrench } from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';

import { URLBuilder } from '@/utils/URLBuilder';
import { cn } from '@/utils/cn';
import { AUCTION_SOURCE_LABELS } from '@/utils/auctions/auctionConfig';
import { formatAuctionDate, formatAuctionDateTime, isPast } from '@/utils/auctions/normalizeDate';
import { getLotTitle } from '@/utils/auctions/auctionSlugs';
import { lotPhotoFor } from '@/utils/auctions/lotPhotoVariant';
import { readDamage } from '@/utils/auctions/lotVocabulary';
import { proposalCardHover, proposalImageHover } from '@/utils/animation/variants';
import type { TAuctionLot } from '@/shared/auctionTypes';

import { AuctionLotStateChip } from './auction-lot-state-chip';
import { AuctionSaleCountdown } from './auction-sale-countdown';
import { Button } from '../ui/button/button';
import { CardAccentLine } from '../cards/shared/card-accent-line';
import { Heading } from '../ui/heading/heading';

/**
 * List item for an automated auction lot (spec 006, FR-007; restyled in 013).
 *
 * Built on the skeleton of the curated proposal card (deleted 2026-09-14 with
 * the rest of the proposals) — wide card, diagonal-cut photo, gold price box,
 * uppercase title, icon chips, date bar with a round arrow, lift-and-glow on
 * hover. The blog card shares the same language.
 *
 * Every field here is optional in the source data — measured coverage runs from
 * 100% (photos, sale date) down to 0% for current bid on IAAI CANADA — so each
 * one is omitted rather than rendered as "0" or "brak" (FR-011).
 */

/**
 * An amount is shown only when its currency is known.
 *
 * A `null` currency means the lot's yard could not be resolved, and an unlabelled
 * or wrongly-labelled figure is worse than none: the same number reads as ~27%
 * different depending on whether it is USD or CAD (FR-008, Principle I).
 */
const formatPrice = (
  value: number | null,
  currency: string | null
): { amount: string; currency: string } | null =>
  value === null || currency === null ? null : { amount: value.toLocaleString('pl-PL'), currency };

const formatOdometer = (value: number | null): string | null =>
  value === null ? null : `${value.toLocaleString('pl-PL')} mi`;

const chipIcon = (Icon: typeof Gauge) => (
  <Icon size={15} className="shrink-0 text-action lg:mr-2 lg:inline-block lg:align-[-3px]" aria-hidden />
);

type TFact = { key: string; name: string; label: string; icon: ReactNode };

type TAuctionLotCard = {
  lot: TAuctionLot;
  /** Listing images above the fold are the LCP element and must not lazy-load. */
  priority?: boolean;
  /**
   * The SERVER's verdict on `isPast(lot.saleDate)`, from the server component
   * that renders the card. The card is a client component, and a clock read
   * during render runs twice — on the server and again in the browser — with
   * two different clocks: the recording's pinned one and the real one outside
   * production, yesterday's and today's across midnight on an ISR page in it.
   * When they disagreed React threw a hydration error and rebuilt the tree
   * (found 2026-09-14 on /aukcje). The first render takes the server's word;
   * after mount the browser re-judges with its own clock.
   */
  saleDayPassed?: boolean;
};

export const AuctionLotCard: FC<TAuctionLotCard> = memo(function AuctionLotCard({ lot, priority = false, saleDayPassed }) {
  const prefersReducedMotion = useReducedMotion();
  const title = getLotTitle(lot);
  const photo = lot.photos[0] ? lotPhotoFor(lot.photos[0], 'card') : null;
  const saleDate = formatAuctionDateTime(lot.saleDate);
  // ~7% of lots the provider still reports as "active" carry a sale date months
  // in the past (measured 13/200, concentrated in IAAI CANADA). Rendering that as
  // an upcoming auction date would mislead a purchase decision, so it is labelled
  // for what it is (FR-012, SC-008, Principle I). Server's word first, then the
  // browser's own clock — see `saleDayPassed`.
  const [isStaleDate, setIsStaleDate] = useState(saleDayPassed ?? isPast(lot.saleDate));

  useEffect(() => {
    setIsStaleDate(isPast(lot.saleDate));
  }, [lot.saleDate]);
  // A lot whose sale has run and is confirmed (spec 015): the card shows what it
  // went for and when, and nothing that invites a bid. The amount is trusted in
  // USD only — the provider's `_usd` fields carry CAD unconverted (spec 007).
  const isSold = lot.saleState === 'finished' && lot.saleResult?.status === 'Sold';
  const soldFor = isSold ? formatPrice(lot.saleResult?.amount ?? null, lot.currency === 'USD' ? 'USD' : null) : null;
  const soldOn = isSold ? formatAuctionDate(lot.saleDate) : null;
  const price = isSold ? null : formatPrice(lot.currentBid, lot.currency);
  const buyNow = isSold ? null : formatPrice(lot.buyNowPrice, lot.currency);
  const odometer = formatOdometer(lot.odometer);

  // The title always carries the year (`getLotTitle` prefixes it and `year` is
  // non-nullable), so the facts are the three the title cannot hold.
  const facts: TFact[] = [];

  if (odometer) facts.push({ key: 'odometer', name: 'Przebieg', label: odometer, icon: chipIcon(Gauge) });
  // Read in Polish where the house's word is known; the raw value otherwise.
  const damage = readDamage(lot.primaryDamage);
  if (damage) {
    facts.push({ key: 'damage', name: 'Uszkodzenie', label: damage.label, icon: chipIcon(Wrench) });
  }
  if (lot.location) {
    facts.push({ key: 'location', name: 'Lokalizacja', label: lot.location, icon: chipIcon(MapPin) });
  }

  const motionProps = prefersReducedMotion
    ? {}
    : {
        initial: 'rest',
        whileHover: 'hover',
        animate: 'rest',
      };

  return (
    <Link href={URLBuilder.paths.getAuctionLot(lot)} className="group block h-full">
      <m.article
        // A fact the gates can count: the label at the bottom reads "Kup teraz"
        // only when the lot has no bid, so it undercounts buy-now lots.
        data-buy-now={lot.buyNowPrice !== null ? '' : undefined}
        variants={prefersReducedMotion ? undefined : proposalCardHover}
        {...motionProps}
        className="relative bg-backgroundLight border border-neutral-800 rounded-xl overflow-hidden flex flex-col lg:flex-row h-full lg:min-h-80 transition-colors hover:border-action/30"
      >
        {/* Photo — the same diagonal cut as the blog card. `lg:h-auto` lets the
            box stretch to the row, and the `fill` image covers whatever height
            the content half needs (provider photos carry no dimensions). */}
        {/* No background on this box: the area outside the diagonal clip must
            show the card's own surface, exactly as on the blog card — a darker
            fill here reads as a badly cut mask. The placeholder paints its own. */}
        <div className="relative lg:basis-1/2 shrink-0 h-56 lg:h-auto overflow-hidden">
          <div className="absolute inset-0 lg:[clip-path:polygon(0_0,100%_0,85%_100%,0_100%)]">
            <m.div
              variants={prefersReducedMotion ? undefined : proposalImageHover}
              className="relative h-full w-full"
            >
              {photo ? (
                <Image
                  src={photo}
                  alt={`${title} — zdjęcie z aukcji`}
                  fill
                  sizes="(max-width: 1023px) 100vw, 33vw"
                  priority={priority}
                  // Served straight from the auction house's CDN (spec 016).
                  // Through the optimizer every photo was fetched and encoded
                  // by the one-vCPU container, whose upstream fetches to
                  // Copart timed out in production; the browser reaches that
                  // CDN faster than the container does.
                  unoptimized
                  className="object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-background text-sm text-neutral-500">
                  Brak zdjęcia
                </div>
              )}
            </m.div>
          </div>

          {/* Automotive gradient overlay */}
          <div className="absolute inset-0 bg-automotive-gradient opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* One strip, cut by the same diagonal seam as the pagination: the
              neutral source half, then the coloured state half on top. With no
              state from the provider the source label stays a plain pill. */}
          <div
            className={cn(
              'absolute top-4 left-4 z-10 inline-flex items-stretch',
              // The strip's blur and shadow live on the wrapper; the halves are
              // opaque, or the overlap along the seam shows through as a wedge.
              lot.saleState && 'rounded-full shadow-lg shadow-black/30 backdrop-blur-md'
            )}
          >
            <span
              className={cn(
                'inline-flex items-center rounded-full border border-white/15 bg-background/80 px-3 py-1.5 text-xs font-oswald uppercase tracking-wider text-neutral-200 backdrop-blur-md shadow-lg shadow-black/30',
                lot.saleState &&
                  '[clip-path:polygon(0_0,100%_0,calc(100%-12px)_100%,0_100%)] rounded-r-none border-r-0 bg-background pr-6 shadow-none backdrop-blur-none'
              )}
            >
              {AUCTION_SOURCE_LABELS[lot.auctionSource]}
            </span>

            <AuctionLotStateChip
              state={lot.saleState}
              stale={isStaleDate}
              opaque
              className="-ml-2.5 [clip-path:polygon(12px_0,100%_0,100%_100%,0_100%)] rounded-l-none border-l-0 pl-6"
            />
          </div>
        </div>

        {/* Content.
            On lg the column reaches 7.5% of the card INTO the photo half — the
            triangle the diagonal cut frees — and a zero-content float with a
            matching `shape-outside` mirrors the cut, so every line box (price,
            title, chips) starts exactly where the photo's edge is at that
            height and the text hugs the slant instead of standing in a plain
            column beside it. Only inline-level content flows around a shape,
            which is why the chips are inline-blocks here and the date bar is
            taken out of flow and pinned to the bottom.
            The float must span the CARD's height, or its slope is steeper than
            the cut: `h-full` resolves against the content box, so the column
            carries no top padding on lg (the title pads itself) and the float
            adds the bottom reserve back. */}
        <div className="relative flex flex-grow flex-col p-5 lg:block lg:basis-1/2 lg:-ml-[7.5%] lg:pt-0 lg:pb-24 lg:pl-4 lg:pr-6">
          <div
            aria-hidden
            className="hidden lg:block float-left h-[calc(100%+6rem)] w-[13%] [shape-outside:polygon(0_0,100%_0,0_100%)] [shape-margin:0.75rem]"
          />

          <Heading
            variant="h3"
            className="text-xl md:text-2xl lg:text-3xl transition-colors group-hover:text-action max-lg:line-clamp-2 mb-3 lg:pt-5"
          >
            {title}
          </Heading>

          {facts.length > 0 && (
            // One fact per line, each line opened by a two-pixel seam skewed
            // like the cut — the seam is the bullet, so no line ever wraps
            // with a stray cut. On lg the items are inline runs ended by a
            // forced break (a block-level `::after` inside the inline), which
            // is what lets each line start where the photo's edge is at that
            // height and step down the slant with the rest of the copy.
            <ul className="mb-4 flex flex-col gap-1.5 text-sm text-neutral-300 lg:mb-2 lg:block lg:leading-7">
              {facts.map(fact => (
                <li
                  key={fact.key}
                  className="flex max-w-full items-center gap-2 lg:inline lg:after:block lg:after:content-['']"
                >
                  {/* On lg, skewed to the CUT's angle, not the buttons' seam: the
                      photo is cut 7.5% of the card over its height, ~12° from
                      vertical, and the lines step down that same slope, so the
                      three seams fall on one line parallel to the cut. Below lg
                      the photo sits above the text with no cut to echo, so the
                      bar stays straight. */}
                  <span
                    aria-hidden
                    className="inline-block h-5 w-0.5 shrink-0 bg-neutral-600 lg:mr-3 lg:-skew-x-[12deg] lg:align-middle"
                  />
                  {fact.icon}
                  <span className="sr-only">{fact.name}: </span>
                  <span className="truncate lg:inline lg:align-middle">{fact.label}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Countdown to the sale at bottom — ticks in the browser, see
              AuctionSaleCountdown for why it is not rendered on the server. */}
          <div className="mt-auto pt-4 border-t border-neutral-800/50 flex flex-wrap items-end justify-between gap-x-4 gap-y-3 lg:absolute lg:bottom-5 lg:left-9 lg:right-6 lg:mt-0">
            {isSold ? (
              <div>
                <p className="font-roboto text-xs uppercase tracking-wider text-neutral-500">Sprzedano</p>
                <p className="font-oswald text-lg leading-tight text-foreground">{soldOn ?? 'data nieznana'}</p>
              </div>
            ) : (
              // Grows into whatever the price block leaves and wraps its own
              // text there, so a long line (the passed-date note) never pushes
              // the price under it (2026-09-14).
              <div className="min-w-0 flex-1 basis-48">
                <AuctionSaleCountdown
                  saleDate={lot.saleDate}
                  saleState={lot.saleState}
                  isStaleDate={isStaleDate}
                  formattedDate={saleDate}
                />
              </div>
            )}

            <div className="ml-auto flex shrink-0 items-end gap-4">
              {/* Auction-side figures only. These are NOT a Polish landed cost,
                  and must never be presented as one (FR-009, FR-010). A label
                  over a bare figure, not a box — the box fought the title. */}
              <div className="text-right">
                {isSold ? (
                  <>
                    <p className="flex items-center justify-end gap-1.5 font-roboto text-xs uppercase tracking-wider text-neutral-500">
                      <Gavel size={12} className="text-action" aria-hidden />
                      Cena sprzedaży
                    </p>
                    {soldFor ? (
                      <p className="font-oswald text-2xl leading-tight text-foreground">
                        {soldFor.amount}
                        <span className="ml-1 text-sm text-neutral-400">{soldFor.currency}</span>
                      </p>
                    ) : (
                      <p className="font-oswald text-sm leading-tight text-neutral-400">Nieopublikowana</p>
                    )}
                  </>
                ) : price || buyNow ? (
                  <>
                    <p className="flex items-center justify-end gap-1.5 font-roboto text-xs uppercase tracking-wider text-neutral-500">
                      {price ? (
                        <Gavel size={12} className="text-action" aria-hidden />
                      ) : (
                        <Tag size={12} className="text-action" aria-hidden />
                      )}
                      {price ? 'Aktualna oferta' : 'Kup teraz'}
                    </p>
                    <p className="font-oswald text-2xl leading-tight text-foreground">
                      {(price ?? buyNow)?.amount}
                      <span className="ml-1 text-sm text-neutral-400">{(price ?? buyNow)?.currency}</span>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-roboto text-xs uppercase tracking-wider text-neutral-500">Cena</p>
                    <p className="font-oswald text-sm leading-tight text-neutral-400">Ustalana na aukcji</p>
                  </>
                )}
              </div>

              {/* Arrow indicator: the Button's face on a span (a button inside a
                link is not allowed), lit by the CARD's hover — fill, sheen and
                all — so the whole card reads as one control. */}
              <Button
                as="span"
                iconOnly
                variant="secondary"
                startIcon={<ChevronRight size={18} aria-hidden />}
                className="h-10 shrink-0 px-4 group-hover:border-action group-hover:bg-action group-hover:text-background group-hover:before:opacity-100 motion-safe:group-hover:before:translate-x-[380%]"
              />
            </div>
          </div>
        </div>

        {/* Golden accent line on left */}
        <CardAccentLine position="left" />
      </m.article>
    </Link>
  );
});
